import { Action } from "redux";

import { bridge, ILocalizationItemsLoadedAction } from "@multiweb/reactCore/bridge";
import { type } from "@multiweb/reactUtils";
import { ILocalizationItem as OriginalLocalizationItem } from "@multiweb/types/contracts";
import { MutableTypedObject, TypedObject } from "@multiweb/types/react";
import { UNKNOWN_KEY_TEXT } from "./constants";
import { ILoadLocalizationKeysAction, ILocalizationKeysLoadedAction, ISetLocaleCodeAction, LocalizationItem, MergedLoadLocalizationActions } from "./types";

const { applyLocParams } = bridge;

/**
 * Zjistí, zda je akce akcí signalizující načtení lokalizačních položek z bridge modulu pod zadaným klíčem. Pouze pro interní použití v i18n modulu.
 * @param fetchingKey Klíč pro indikaci lokalizací.
 */
export const createAreLocalizationItemsLoadedActionPattern = (fetchingKey: string) => (action: Action) => (
    action instanceof ILocalizationItemsLoadedAction && action.payload.key === fetchingKey
);

/**
 * Zjistí, zda je akce akcí signalizující dokončení načítání lokalizačních klíčů načítaných pod zadaným klíčem.
 * @param fetchingKey Klíč pro identifikaci lokalizací.
 */
export const createAreLocalizationKeysLoadedActionPattern = (fetchingKey: string) => (action: Action) => (
    action instanceof ILocalizationKeysLoadedAction && action.payload.fetchingKey === fetchingKey
);

/**
 * Zjistí, zda se jedná o akci s požadavkem na načtení lokalizačních klíčů a zda režim batchování odpovídá zadanému parametru.
 * Umožňuje tak rozlišit akce se zapnutým/vypnutým batchováním.
 * @param batchingEnabled Pokud je true, odchytávají se akce se zapnutým batchováním. Pokud je false, odchytávají se akce s vypnutým batchováním.
 */
export const createLoadLocalizationKeysActionPattern = (batchingEnabled: boolean) => (action: Action) => (
    action instanceof ILoadLocalizationKeysAction && action.payload.enableBatching === batchingEnabled
);

/**
 * Zjistí, zda se jedná o akci s požadavkem na změnu jazyka a zda je jazyk jiný než aktuálně nastavený.
 * @param localeCode Odchytávají se akce, které mají jiný localeCode.
 */
export const createSetLocaleCodeActionPattern = (localeCode: string) => (action: Action) => (
    action instanceof ISetLocaleCodeAction && action.payload.localeCode !== localeCode
);

/**
 * Pomocná funkce, která transformuje načtené lokalizační položky na objekt klíčovaný přes lokalizační klíče.
 * Pro nenačtené klíče se doplní lokalizační položka, jejíž hodnoty jsou undefined.
 * @param localizationKeys Seznam lokalizačních klíčů, pro které měla být lokalizace načtena.
 * @param loadedLocalizationItems Mapa načtených lokalizačních položek.
 */
export const transformLoadedLocalizationItems = (localizationKeys: readonly string[], loadedLocalizationItems: ReadonlyMap<string, OriginalLocalizationItem>): TypedObject<LocalizationItem> => (
    localizationKeys.reduce((localizations, localizationKey) => {
        const localizationItem = loadedLocalizationItems.get(localizationKey);
        localizations[localizationKey] = localizationItem
            ? {
                primaryText: localizationItem.primaryText,
                secondaryText: localizationItem.secondaryText,
            }
            : {
                primaryText: UNKNOWN_KEY_TEXT,
                secondaryText: undefined,
            };
        return localizations;
    }, {} as MutableTypedObject<LocalizationItem>)
);

/**
 * Vrací primární nebo sekundární text zadané položky - včetně nahrazení placeholderů.
 * - Pokud položka lokalizace není zadána, vrací `undefined`.
 * - Pokud položka zadána je, ale nemá vyplněn text pro `itemKey`, vrací konstantu pro neznámou lokalizaci.
 * @param localizationItem Položka, ze které bude text vrácen.
 * @param itemKey Identifikace textu - `primaryText` nebo `secondaryText`.
 * @param params Parametry lokalizace - pro nahrazení za placeholdery.
 */
export const getText = (localizationItem: LocalizationItem | undefined, itemKey: keyof LocalizationItem, params?: string[]) => {
    if (!localizationItem) {
        return undefined;
    }
    const localizationText = localizationItem[itemKey];
    return type.isString(localizationText) ? applyLocParams(localizationText, params) : UNKNOWN_KEY_TEXT;
};

/**
 * Spojí lokalizační a načítací klíče ze zadaných akcí. Výsledný objekt obsahuje pouze unikátní výskyty, duplicity jsou odstraněny.
 * @param actions Akce, z nichž se získají data.
 */
export const mergeLoadLocalizationKeysActions = (actions: readonly ILoadLocalizationKeysAction[]): MergedLoadLocalizationActions => {
    const mergedData = actions.reduce((result, action) => {
        const { fetchingKey, localizationKeys } = action.payload;
        return {
            fetchingKeys: [...result.fetchingKeys, fetchingKey],
            locKeys: [...result.locKeys, ...localizationKeys],
        };
    }, {
        fetchingKeys: [],
        locKeys: [],
    } as MergedLoadLocalizationActions);

    return {
        fetchingKeys: [...new Set(mergedData.fetchingKeys)],
        locKeys: [...new Set(mergedData.locKeys)],
    };
};
